import { useEffect, useState } from "react";
import { Menu, X, Instagram, Mail, Linkedin } from "lucide-react";
import { site } from "../../lib/siteConfig";

const WhatsAppGlyph = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const links = [
  { href: "#chi-sono", label: "Chi sono" },
  { href: "#servizi", label: "Servizi" },
  { href: "#portfolio", label: "Portfolio" },
  { href: "#dove-lavoro", label: "Dove lavoro" },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-brand-line/60 shadow-sm"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" className="flex flex-col leading-none group">
          <span className="font-display text-2xl tracking-tight text-brand-ink group-hover:text-brand-inkHover transition-colors">
            Gorla
            <span className="italic font-normal"> Properties</span>
          </span>
          <span className="mt-0.5 text-[10px] uppercase tracking-[0.28em] text-brand-muted">
            Rendita immobiliare
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace(/[#/]/g, "")}`}
              className="relative text-sm font-medium text-brand-text hover:text-brand-ink transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-brand-ink hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-1.5">
          <a
            href={site.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp"
            aria-label="WhatsApp"
            className="h-10 w-10 inline-flex items-center justify-center text-[#25D366] hover:bg-[#25D366]/10 rounded-sm transition-colors"
          >
            <WhatsAppGlyph size={18} />
          </a>
          <a
            href={site.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram"
            aria-label="Instagram"
            className="h-10 w-10 inline-flex items-center justify-center text-brand-ink hover:bg-brand-beige/50 rounded-sm transition-colors"
          >
            <Instagram size={18} strokeWidth={1.8} />
          </a>
          <a
            href="#contatti"
            data-testid="nav-email"
            aria-label="Scrivimi un messaggio"
            className="h-10 w-10 inline-flex items-center justify-center text-brand-ink hover:bg-brand-beige/50 rounded-sm transition-colors"
          >
            <Mail size={18} strokeWidth={1.8} />
          </a>
          <a
            href={site.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-calendly"
            className="group ml-2 inline-flex items-center gap-2 bg-brand-ink text-white text-sm font-medium px-5 py-3 rounded-sm hover:bg-brand-inkHover transition-all"
          >
            Prenota una call
            <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-brand-ink"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-white border-t border-brand-line">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.href.replace(/[#/]/g, "")}`}
                className="text-brand-text text-base font-medium"
              >
                {l.label}
              </a>
            ))}
            <a
              href={site.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-cta"
              onClick={() => setOpen(false)}
              className="mt-2 text-center bg-brand-ink text-white text-sm font-medium px-5 py-3 rounded-sm"
            >
              Prenota una call
            </a>
            <div className="mt-3 pt-4 border-t border-brand-line flex items-center gap-3">
              <a
                href={site.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-whatsapp"
                aria-label="WhatsApp"
                onClick={() => setOpen(false)}
                className="h-11 w-11 inline-flex items-center justify-center bg-[#25D366] text-white rounded-sm"
              >
                <WhatsAppGlyph size={18} />
              </a>
              <a
                href={site.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-instagram"
                aria-label="Instagram"
                onClick={() => setOpen(false)}
                className="h-11 w-11 inline-flex items-center justify-center border border-brand-line text-brand-ink rounded-sm"
              >
                <Instagram size={18} strokeWidth={1.8} />
              </a>
              <a
                href={site.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-linkedin"
                aria-label="LinkedIn"
                onClick={() => setOpen(false)}
                className="h-11 w-11 inline-flex items-center justify-center border border-brand-line text-brand-ink rounded-sm"
              >
                <Linkedin size={18} strokeWidth={1.8} />
              </a>
              <a
                href="#contatti"
                data-testid="nav-mobile-email"
                aria-label="Scrivimi un messaggio"
                onClick={() => setOpen(false)}
                className="h-11 w-11 inline-flex items-center justify-center border border-brand-line text-brand-ink rounded-sm"
              >
                <Mail size={18} strokeWidth={1.8} />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
